import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, worklistHandoff, type Huddle, type ShiftOverview } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { useReadOnly } from '../../ui/ReadOnly';
import { clock24, newId, type Task } from './careUtil';
import { useBuildLabels } from '../../ui/buildLabels';

// The Care Staff To do tab (was Huddle), Console V4 (design csSecIsHuddle, T6333-6431,
// logic L44781-44811 and L45028-45047): the end of the shift on the left (still open,
// passed to coworkers, waiting on the nurse, notes, Sign and hand off) and What changed
// today on the right (the residents the detectors want seen, as the Huddle listed them).

export interface NurseRow { who: string; what: string; red: boolean }

const BAND_INK = { CRITICAL: 'var(--color-red)', URGENT: 'var(--color-slate-deep)', ROUTINE: 'var(--color-muted)' } as const;
const BAND_TINT = { CRITICAL: 'rgba(var(--rgb-red), .1)', URGENT: 'rgba(var(--rgb-slate-deep), .1)', ROUTINE: 'rgba(84,96,108,.1)' } as const;
const AWARE = {
  act: ['var(--color-red)', 'rgba(var(--rgb-red), .078)'],
  watch: ['var(--color-slate-deep)', 'rgba(var(--rgb-slate-deep), .078)'],
  muted: ['var(--color-muted)', 'rgba(84,96,108,.078)'],
} as const;
const ORDER = { CRITICAL: 0, URGENT: 1, ROUTINE: 2 } as const;

export function CareTodo({
  s, open, late, now, shiftEnd, next, handed, nurseLocal, huddleDone, onHuddleDone,
}: {
  s: ShiftOverview;
  open: Task[];
  late: number;
  now: number;
  shiftEnd: number | null;
  next: string;
  handed: Record<string, { offerId: string; to: string; at: string; accepted: boolean }>;
  nurseLocal: NurseRow[];
  huddleDone: Record<string, boolean>;
  onHuddleDone: (residentId: string, v: boolean) => Promise<void>;
}) {
  const build = useBuildLabels();
  const { client } = useSession();
  const readOnly = useReadOnly();
  const demoBuilding = useDemoBuilding();
  const [note, setNote] = useState('');
  const [notes, setNotes] = useState<string[]>([]);
  const [signed, setSigned] = useState<{ id: string; at: string } | null>(s.signoff ? { id: s.signoff.id, at: clock24(Date.parse(s.signoff.signedAt)) } : null);
  const nurse: NurseRow[] = [...(s.nurse ?? []).map((n) => ({ who: n.who, what: n.what, red: n.kind === 'escalation' })), ...nurseLocal];
  const passedIds = Object.keys(handed);
  const byId = new Map([...s.tasks, ...open].map((t) => [t.id, t]));
  const passedBy = new Map((s.passed ?? []).map((p) => [p.taskId, p]));
  const sorted = open.slice().sort((a, b) => Date.parse(a.due) - Date.parse(b.due));
  const h = s.handoff;
  const noteRows = [
    ...(h?.notes ?? []).map((n) => ({ who: n.who, note: n.note, to: n.residentId ? '/residents' : '/worklist' })),
    ...notes.map((n) => ({ who: `You · ${clock24(now)}`, note: n, to: '/worklist' })),
  ];
  const meta = h
    ? `from ${h.from} · written ${clock24(Date.parse(h.writtenAt))} · kept ${h.keptHours}h${h.real || demoBuilding || !build ? '' : ' · SAMPLE'}${notes.length ? ` · ${notes.length} added by you` : ''}`
    : `no handoff from the last shift${notes.length ? ` · ${notes.length} added by you` : ''}`;
  const endAt = shiftEnd !== null ? clock24(shiftEnd) : '–';

  return (
    <div className="cs4-todo">
      <div className="cs4-todo__col">
        <div className="cs4-todo__head">
          <span className="cs4-todo__h">To do</span>
          <span className="cs4-todo__line">{`Hands off to ${next} at ${endAt} · ${open.length} still open · ${late} late · ${passedIds.length} passed on · ${nurse.length} with the nurse`}</span>
        </div>
        <div className="cs4-todo__block">
          <div className="cs4-todo__k">Still open</div>
          {sorted.slice(0, 5).map((w) => (
            <div key={w.id} className="cs4-todo__row">
              <span className={`cs4-todo__at ${Date.parse(w.due) < now ? 'cs4--late' : 'cs4--ahead'}`}>{clock24(Date.parse(w.due))}</span>
              <span className="cs4-todo__who">{`${w.name}${w.room ? ` · Rm ${w.room}` : ''}`}</span>
              <span className="cs4-todo__what">{w.what}</span>
            </div>
          ))}
          <span className="cs4-todo__more">{open.length > 5 ? `+${open.length - 5} more carry over to ${next} automatically` : ''}</span>
        </div>
        <div className="cs4-todo__block">
          <div className="cs4-todo__k">Passed to coworkers</div>
          {passedIds.length === 0 && <span className="cs4-todo__none">Nothing passed on. Use "Can't get to it" on a row.</span>}
          {passedIds.map((id) => {
            const w = byId.get(id) ?? passedBy.get(id);
            const p = handed[id];
            return (
              <div key={id} className="cs4-todo__row">
                <span className="cs4-todo__pwho">{w?.name && w.what ? `${w.name} · ${w.what}` : 'A stop on your shift'}</span>
                <span className="cs4-todo__pto">{`${p.accepted ? 'Accepted by ' : 'Offered to '}${p.to} · ${p.at}`}</span>
              </div>
            );
          })}
        </div>
        <div className="cs4-todo__block">
          <div className="cs4-todo__k">Waiting on the nurse</div>
          {nurse.length === 0 && <span className="cs4-todo__none">Nothing flagged. Refusals and out-of-range vitals land here.</span>}
          {nurse.map((n, i) => (
            <Link key={i} to="/worklist" className="cs4-todo__row">
              <span className={`cs4-todo__dot${n.red ? ' cs4-todo__dot--red' : ''}`} />
              <span className="cs4-todo__nwho">{n.who}</span>
              <span className="cs4-todo__nwhat">{n.what}</span>
            </Link>
          ))}
        </div>
        <div className="cs4-todo__block">
          <div className="cs4-todo__k">{`Notes · ${meta}`}</div>
          {noteRows.map((r, i) => (
            <span key={i} className="cs4-note">
              <Link to={r.to} className="cs4-note__who">
                {r.who}
              </Link>
              <span className="cs4-note__text">{r.note}</span>
            </span>
          ))}
          <form
            className="cs4-note__add"
            onSubmit={async (e) => {
              e.preventDefault();
              const t = note.trim();
              if (!t) return;
              await worklistHandoff(client, { body: { summary: t } });
              setNotes((n) => [...n, t]);
              setNote('');
            }}
          >
            <input className="cs4-note__input" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder={`Note for ${next}…`} aria-label={`Note for ${next}`} disabled={readOnly} />
            <button type="submit" className={`cs4-note__btn${note.trim() ? ' cs4-note__btn--on' : ''}`} disabled={readOnly || !note.trim()}>
              Add
            </button>
          </form>
        </div>
        {signed ? (
          <span className="cs4-signed">
            {`Signed ${signed.at} by you. ${next} see this first. Saved to the Truth Ledger.`}
            <button
              type="button"
              className="cs4-link"
              disabled={readOnly}
              onClick={async () => {
                await api.shiftSignoffReopen(client, { body: { id: signed.id } });
                setSigned(null);
              }}
            >
              Reopen
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="cs4-sign"
            disabled={readOnly}
            onClick={async () => {
              const r = await api.shiftSignoff(client, { body: { clientId: newId(), notes } });
              setSigned({ id: r.id, at: clock24(Date.parse(r.signed_at)) });
            }}
          >
            {`Sign and hand off to ${next}`}
          </button>
        )}
      </div>
      <div className="cs4-todo__col cs4-todo__col--right">
        <div className="cs4-todo__h">What changed today</div>
        {s.huddleList ? <WhatChanged h={s.huddleList} now={now} done={huddleDone} onDone={onHuddleDone} /> : <div className="cs4-wc__note">Nothing from the detectors for your residents this shift.</div>}
      </div>
    </div>
  );
}

function WhatChanged({ h, now, done, onDone }: { h: Huddle; now: number; done: Record<string, boolean>; onDone: (residentId: string, v: boolean) => Promise<void> }) {
  const readOnly = useReadOnly();
  const demoBuilding = useDemoBuilding();
  const isDone = (r: Huddle['rows'][number]) => done[r.residentId] ?? r.doneAt !== null;
  const rows = h.rows.slice().sort((a, b) => (isDone(a) ? 1 : 0) - (isDone(b) ? 1 : 0) || ORDER[a.band] - ORDER[b.band]);
  const left = rows.filter((r) => !isDone(r));
  const crit = left.filter((r) => r.band === 'CRITICAL').length;
  const s0 = Date.parse(h.window.start);
  const s1 = Date.parse(h.window.end);
  const through = Math.max(0, Math.min(1, (now - s0) / Math.max(1, s1 - s0)));
  const leftMin = Math.max(0, Math.floor((s1 - now) / 60000));
  const tone = crit ? 'act' : left.length ? 'watch' : 'good';
  return (
    <>
      <div className="cs4-wc__box">
        <div className="cs4-wc__top">
          <span className={`cs4-wc__line cs4-wc--${tone}`}>{left.length ? `${left.length}${left.length === 1 ? ' resident still needs you' : ' residents still need you'}` : 'Everyone on your list is done'}</span>
          <span className="cs4-wc__left">
            <span className={`cs4-wc__lv cs4-wc--${tone}`}>{`${Math.floor(leftMin / 60)}h ${leftMin % 60}m`}</span>
            <span className="cs4-wc__lk">left on shift</span>
          </span>
        </div>
        <div className="cs4-wc__track">
          <span className={`cs4-wc__bar${crit ? ' cs4-wc__bar--act' : ''}`} style={{ width: `${Math.round(through * 100)}%` }} />
        </div>
        <div className="cs4-wc__sub">
          {crit
            ? `${crit}${crit === 1 ? ' is critical, that one first, before anything else on the round.' : ' are critical, those first, before anything else on the round.'}`
            : left.length
              ? 'Nothing critical. Work down the list in order.'
              : 'Anything not cleared carries to the next shift automatically.'}
        </div>
      </div>
      <div className="cs4-wc__aware">
        {h.aware.map((a) => (
          <span key={a.k} className="cs4-wc__chip" style={{ color: AWARE[a.tone][0], background: AWARE[a.tone][1] }}>
            <span className="cs4-wc__chipn">{a.n}</span>
            {a.k}
          </span>
        ))}
      </div>
      <div className="cs4-wc__rows">
        {rows.map((r) => {
          const d = isDone(r);
          return (
            <div key={r.residentId} className={`cs4-wc__card${d ? ' cs4-wc__card--done' : ''}`}>
              <div className="cs4-wc__ch">
                <span className="cs4-wc__band" style={{ color: BAND_INK[r.band], background: BAND_TINT[r.band] }}>
                  {r.band}
                </span>
                <span className="cs4-wc__who">{r.name}</span>
                <span className="cs4-wc__room">{`Room ${r.room ?? '–'}`}</span>
              </div>
              <div className="cs4-wc__changed">{r.changed}</div>
              <div className="cs4-wc__means">{r.means}</div>
              <div className="cs4-wc__window" style={{ color: r.band === 'CRITICAL' ? 'var(--color-red)' : r.band === 'URGENT' ? 'var(--color-slate-deep)' : 'var(--color-ink2)' }}>
                {r.window}
              </div>
              <div className="cs4-wc__acts">
                <button type="button" className={`cs4-wc__done${d ? ' cs4-wc__done--on' : ''}`} disabled={readOnly} onClick={() => onDone(r.residentId, !d)}>
                  {/* V9: one word per action (Done finishes a task). */}
                  {d ? 'Done ✓' : 'Done'}
                </button>
                <Link to="/guardian" className="cs4-wc__nurse">
                  Get a nurse
                </Link>
                <span className="cs4-wc__foot">{r.foot}</span>
              </div>
            </div>
          );
        })}
      </div>
      {left.length === 0 && <div className="cs4-wc__all">Everyone on your unit has been seen this shift. Nothing is carrying over.</div>}
      <div className="cs4-wc__carry">
        <div className="cs4-wc__ck">Carries to the next shift</div>
        <div className="cs4-wc__cv">{left.length ? `${left.map((r) => r.name).join(', ')}, not yet cleared. The next shift sees this list, with what changed and what was asked for.` : 'Nothing outstanding. The next shift starts clean.'}</div>
      </div>
      <div className="cs4-wc__note">
        {h.real || demoBuilding
          ? 'Indicators read each resident against their own baseline; they are not diagnoses and never act on their own. You decide; Atlas only tells you what changed.'
          : 'SAMPLE. Indicators read each resident against their own baseline; they are not diagnoses and never act on their own. You decide; Atlas only tells you what changed.'}
      </div>
    </>
  );
}
